<template>
  <section :class="`screen-${currentScreen}`">
    <section v-if="currentScreen === 'get_code'">
      <div class="sms-phone"></div>
      <h4>{{ i18n.t('phoneVerification:verify') }} {{ phoneNumber }} <router-link :to="screenParams.associatedPhoneNumberUrl">{{ i18n.t('base:edit') }}</router-link></h4>

      <div class="message-body">
        <p>{{ i18n.t('phoneVerification:will_send') }}</p>
      </div>

      <transition name="bottom" appear>
        <action-bar>
          <ul>
            <li @click="getCode()" class="primary">
              <span>{{ i18n.t('phoneVerification:get_code') }}</span>
            </li>
          </ul>
        </action-bar>
      </transition>
    </section>

    <section v-if="currentScreen === 'generating'">
      <div class="generating"></div>
      <p class="message-body generate">{{ i18n.t('phoneVerification:generating') }}...</p>

      <transition name="bottom" appear>
        <action-bar v-if="screens.generating.showNext">
          <ul>
            <li @click="showScreen('enter_code')" class="primary">
              <span>{{ i18n.t('phoneVerification:enter_next_step') }}</span>
            </li>
          </ul>
        </action-bar>
      </transition>
    </section>

    <section v-if="currentScreen === 'enter_code'">
      <one-line-form :submit="submitCode" :submittable="!!smsCode && isValid && !showWarning" :saving="isSaving ? smsCode : ''" :loading="isSaving">
        <template slot="underlay">
          <transition name="fade">
            <div class="input">
              <ins v-if="showWarning">
                {{ smsCode }}
                <span>
                  {{ i18n.t('phoneVerification:wrong_code') }}
                </span>
              </ins>
            </div>
          </transition>
        </template>
        <input-field name="code"
                     :class="{ invisible: isSaving, warning: showWarning }"
                     type="text"
                     :title="showWarning ? '' : i18n.t('phoneVerification:activation_code')"
                     :placeholder="i18n.t('phoneVerification:enter_activation_code')"
                     :autocapitalize="true"
                     :autocorrect="false"
                     :disabled="showWarning"
                     v-model="smsCode" />
      </one-line-form>
      <helper v-show="!showWarning" class="hint">{{ i18n.t('phoneVerification:sent_message') }}</helper>
      <helper v-show="isSaving" class="verification-indicator">{{ i18n.t('base:verifying') }}</helper>
      <helper v-show="askAboutResend && !isSaving && !showWarning" :questions="true" :class="{ 'code-resend': true, android: app.deviceIsAndroid}"><a @click="newCode">{{ i18n.t('phoneVerification:resend_code') }}</a></helper>
      <transition name="fade">
        <div class="warning-info" v-show="showWarning">
          <p>
            {{i18n.t('phoneVerification:sent_to')}} <br>
            <span class="phone">{{ phoneNumber }}</span>
            <router-link class="edit" :to="screenParams.associatedPhoneNumberUrl">{{ i18n.t('base:edit') }}</router-link>
          </p>
        </div>
      </transition>
      <transition name="bottom" appear>
        <action-bar v-if="showWarning">
          <ul>
            <li @click="newCode" class="primary">
              <span>{{ i18n.t('phoneVerification:get_new_code') }}</span>
            </li>
          </ul>
        </action-bar>
      </transition>
      <show-keyboard v-if="!showWarning"></show-keyboard>
    </section>
  </section>
</template>

<script>
  import { mapState, mapMutations } from 'vuex'

  import Breakpoints from '../mixins/breakpoints'
  import AdditionalKeyboard from '../components/AdditionalKeyboard'

  export default {
    components: {
      'additional-keyboard': AdditionalKeyboard
    },
    props: ['screenParams'],
    mixins: [
      Breakpoints
    ],
    data () {
      const phoneNumber = this.$store.state.user.phoneE164
      const phoneVerification = this.$store.state.phoneVerification
      const phoneNumberCountryCode = this.$store.state.user.phoneCountry

      return {
        phoneNumber,
        phoneNumberCountryCode,
        OTPDeliveryMethod: '',
        smsCode: null,
        isSaving: false,
        currentScreen: 'get_code',
        screens: {
          'get_code': {},
          'generating': {},
          'enter_code': {}
        },
        verificationId: phoneVerification.verificationId,
        showWarning: false,
        askAboutResend: false,
        resendTimout: null
      }
    },
    created () {
      this.mobileOTPDeliveryMethod()
    },
    computed: {
      ...mapState(['app', 'partner']),
      isValid () {
        return /^(([a-zA-Z]{1}[\d]{1})|([\d]{1}[a-zA-Z]{1})|([a-zA-Z]{1}[\d+]{3}))$/.test(this.smsCode)
      },
      keyboardHeight () {
        const isIOS11 = navigator.userAgent.match(/Mobile\/15A372/i)

        if (isIOS11) {
          return 285
        }

        return null
      }
    },
    watch: {
      showWarning () {
        if (this.showWarning && document.activeElement.nodeName.toLowerCase() === 'input') {
          document.activeElement.blur()
        }
      },

      verificationId () {
        if (this.verificationId) {
          this.updatePhoneVerification({
            verificationId: this.verificationId
          })
        }
      }
    },
    methods: {
      ...mapMutations(['updatePhoneVerification']),
      mobileOTPDeliveryMethod () {
        const OTPDeliveryMethod = this.partner.forceMobileOTPDeliveryMethod

        if (typeof OTPDeliveryMethod === 'object') {
          this.OTPDeliveryMethod = OTPDeliveryMethod[this.phoneNumberCountryCode] ? OTPDeliveryMethod[this.phoneNumberCountryCode] : ''
        } else {
          this.OTPDeliveryMethod = OTPDeliveryMethod || ''
        }
      },
      showScreen (screenName) {
        this.currentScreen = screenName

        if (screenName === 'enter_code') {
          const input = this.$el.querySelector('input')
          this.showDelayedResend()

          if (input) {
            input.focus()

            this.$nextTick(() => {
              input.addEventListener('keydown', () => {
                window.clearTimeout(this.resendTimeout)
                this.showDelayedResend()
              })
            })
          }
        }
      },

      getCode (data) {
        this.showScreen('generating')

        if (this.verificationId) {
          return this.resendCode()
        }

        this.$rmt.trackEvent({
          category: 'otp',
          action: 'send request'
        })

        setTimeout(() => {
          this.showScreen('enter_code')
        }, 5000)

        // call phones API to send sms

        this.$phonesService
          .create({
            partnerName: this.partner.partnerName,
            codePrefixMessage: `${this.i18n.t('phoneVerification:activation_code')}:`,
            phone: this.phoneNumber,
            delivery: this.partner.forceMobileOTPDeliveryMethod[this.phoneNumberCountryCode] ? this.partner.forceMobileOTPDeliveryMethod[this.phoneNumberCountryCode] : '',
            language: this.app.moduleLanguage
          })
          .then((res, err) => {
            const codeSent = res.statuses.find(status => status.status === 'SENT')

            if (codeSent) {
              this.$rmt.trackEvent({
                category: 'otp',
                action: 'send'
              })

              this.verificationId = res.id
            }
          })
          .catch(() => {})
      },

      resendCode () {
        this.$rmt.trackEvent({
          category: 'otp',
          action: 'resend request'
        })

        setTimeout(() => {
          this.showScreen('enter_code')
        }, 5000)

        // Call phones API to resend code
        this.$phonesService
          .patch(this.verificationId, {
            codePrefixMessage: `${this.i18n.t('phoneVerification:activation_code')}:`,
            phone: this.phoneNumber,
            resendCode: true,
            delivery: this.partner.forceMobileOTPDeliveryMethod[this.phoneNumberCountryCode] ? this.partner.forceMobileOTPDeliveryMethod[this.phoneNumberCountryCode] : '',
            language: this.app.moduleLanguage
          })
          .then((res, err) => {
            const codeSent = res.statuses.find(status => status.status === 'SENT')

            if (codeSent) {
              this.$rmt.trackEvent({
                category: 'otp',
                action: 'resend'
              })
            }
          })
          .catch(() => {})
      },

      submitCode () {
        this.$rmt.trackEvent({
          category: 'otp',
          action: 'verification'
        })
        this.isSaving = true

        // Call phones API to verify submitted code
        this.$phonesService
          .update(this.verificationId, {
            code: this.smsCode.toUpperCase()
          })
          .then((res, err) => {
            const isValid = res.statuses.find(status => status.status === 'VALID')

            if (isValid) {
              this.$rmt.trackEvent({
                category: 'otp',
                action: 'success'
              })

              this.$store.dispatch('updateUser', {
                phoneVerificationToken: res.token,
                phoneVerificationId: res.id
              })

              return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
            }

            this.isSaving = false
            this.showWarning = true
          })
          .catch(() => {
            this.isSaving = false
            this.showWarning = true
          })

        this.$rmt.trackEvent({
          category: 'otp',
          action: 'fail'
        })
      },

      newCode () {
        this.showWarning = false
        this.smsCode = ''

        this.showScreen('get_code')
      },

      showDelayedResend () {
        this.resendTimeout = setTimeout(() => {
          this.askAboutResend = true
        }, 4000)
      }
    }
  }
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  ul {
    @include button-list;

    li{
      text-align: center;
      padding: 0;

      &.primary {
        @include desktop-big {
          max-width: calc(100% - 50px);
        }
      }
    }
  }

  section {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;

    section {
      width: 100%;
    }

    &.screen-enter_code {
      section {
        height: calc(100% - 200px);
        justify-content: center;
      }
    }
  }

  @include desktop {
    .screen .content .holder > section.screen-get_code {
      margin-top: 100px;
    }
    .screen .content .holder > section.screen-generating {
      margin-top: 100px;
    }
  }

  .sms-phone {
    background: url('~static/sms.svg');
    background-repeat: no-repeat;
    background-size: cover;
    height: 125px;
    width: 95px;

    @include desktop {
      margin-top: -35px;
    }
  }

  .actions:not(.show-keyboard) {
    @include desktop-big {
      border-top: none;
    }
  }

  .generating {
    background: url('~static/generate.svg');
    background-repeat: no-repeat;
    background-size: cover;
    height: 111px;
    width: 141px;
    margin-bottom: 60px;
  }

  h4, h4 > a {
	  color: #161616;
	  font-size: 1.5625rem;
    margin-top: 40px;

    @include desktop {
      margin-top: 30px;
      font-size: 1.5rem;
    }
  }

  h4{
    font-weight: bold;
    width: 100%;
    text-align: left;
  }

  h4 > a {
    font-weight: 300;
    font-size: 16px;
    position: relative;
    top: -3px;
  }

  p {
    text-align: left;
    color: #444F4F;
    font-size: 16px;
    line-height: 20px;
    font-weight: 300;
  }

  .message-body {
    margin-top: 30px;

    &.generate {
      margin-top: 0px;
    }

    @include desktop {
      margin-top: 25px;
    }

    p {
      white-space: pre-line;
    }
  }

  .verification-indicator {
    font-size:16px;
    line-height: 16px;
  }

  ins {
     color: transparent;
    background: #FFE676;
    display: inline-block;
    position: relative;

    span {
      background: no-repeat left 9px;
      background-image: url('~static/warning_pointer.svg');
      position: absolute;
      display: block;
      left: 40px;
      bottom: 100%;
      height: 40px;
      color: $inputWarningColor;
      color: $inputWarningColor-portal;
      font-size: 1rem;
      font-weight: 300;
      padding-left: 42px;
      margin-bottom: 10px;
      line-height: 120%;
    }
  }

  .actions{
    z-index: 500;
  }

  .warning-info {
    position: absolute;
    bottom: 150px;
    width: 100%;
    left: 0;
    font-family: $mainFontFamily;
    font-family: $mainFontFamily-portal;
    line-height: 25px;

    @media (max-width: #{$small-phone-width}) {
      line-height: 21px;
      bottom: 120px;
    }

    @include desktop {
      bottom: 120px;
    }

    h3, p {
      text-align: left;
    }

    h3 {
      font-family: $hintFontFamily;
      font-family: $hintFontFamily-portal;
      color: $inputRegularHeaderColor;
      color: $inputRegularHeaderColor-portal;
      font-size: $inputRegularHeaderSize;
      font-size: $inputRegularHeaderSize-portal;
      font-weight: $inputRegularHeaderWeight;
      font-weight: $inputRegularHeaderWeight-portal;
    }

    p {
      font-family: $hintFontFamily;
      font-family: $hintFontFamily-portal;
      color: $inputRegularBodyColor;
      color: $inputRegularBodyColor-portal;
      font-size: $inputRegularBodySize;
      font-size: $inputRegularBodySize-portal;
      font-weight: $inputRegularBodyWeight;
      font-weight: $inputRegularBodyWeight-portal;
      line-height: 1.5em;
      .edit {
        color: $inputRegularBodyColor;
      }
    }

    .phone {
      font-weight: $boldFontWeight;
      font-weight: $boldFontWeight-portal;
    }
  }

  .code-resend {
    text-align: left;
    margin-top: 50px;

    @include desktop {
      margin-top: 20px;
    }

    a {
      font-size: $inputLinkSize;
      font-size: $inputLinkSize-portal;
      color: $inputLinkColor;
      color: $inputLinkColor-portal;
      font-weight: $inputLinkWeight;
      font-weight: $inputLinkWeight-portal;
      text-decoration: underline;
    }
  }
</style>



// WEBPACK FOOTER //
// src/screens/SmsCode.vue